import React, { useCallback, useEffect, useState, useRef } from "react";
import Layout1 from "../../DataLayouts/Layout1.jsx";
import NextWeekIcon from "@mui/icons-material/NextWeek";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import useAuthStore from "../../../Zustand/Store/useAuthStore.js";
import { fetchWorkShiftTypes } from "../../../Apis/Workshift-api.js";
import axios from "axios";
import toast from "react-hot-toast";
import { MAIN_URL } from "../../../Configurations/Urls.js";
import { fetchUserTypes } from "../../../Apis/UserTypes.js";
import { Description } from "@mui/icons-material";
import Layout4 from "../../DataLayouts/Layout4.jsx";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { DateRangeIcon } from "@mui/x-date-pickers";
import { fetchDocuments, fetchDocumentTypes } from "../../../Apis/Documents.js";
import PersonIcon from "@mui/icons-material/Person";
import CategoryIcon from "@mui/icons-material/Category";
import TableDataGeneric from "../../../Configurations/TableDataGeneric.js";
import { operatorRequiresValue } from "../../../Components/filters/operators";
import {
  applyClientToolbarFilters,
  asOptionList,
  mergeToolbarFilters,
  ToolbarFilterSelect,
  useToolbarFilterChange,
} from "../../../Components/Table/toolbarFilters";

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "person_document_id",
  sortOrder: "desc",
};

const DOCUMENT_TOOLBAR_FILTERS = [
  { urlKey: "document_type_id", field: "person_document_type_id" },
];

const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({ field, operator, value: filterValue });
    }
  });

  return filters;
};

const DEFAULT_COLUMNS = [
  {
    field: "Employee_name",
    label: "Employee Name",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "emp_code",
    label: "Employee Code",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "document_name",
    label: "Document Name",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "document_type",
    label: "Document Type",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
];

export  default function Documentist() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);

  const { userData } = useAuthStore();
  const org = userData?.organization;

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);
  const [documentTypes, setDocumentTypes] = useState([]);
  const handleToolbarFilterChange = useToolbarFilterChange(setSearchParams);

  // Load table configuration from general-datagrids API
  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false);
        return;
      }

      try {
        const configRes = await fetch(`${MAIN_URL}/api/general-datagrids`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const datagrids = configResponse.datagrids || [];
          const orgKey = `employee_documents_grid_${org.organization_id}`;
          const savedConfig = datagrids.find(
            (dg) => dg.datagrid_key === orgKey
          );

          if (savedConfig) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);

            if (
              serverCfg?.columns &&
              Array.isArray(serverCfg.columns) &&
              serverCfg.columns?.length > 0
            ) {
              setConfigColumns(serverCfg.columns);
            } else {
              setConfigColumns(DEFAULT_COLUMNS);
            }
          } else {
            setConfigColumns(DEFAULT_COLUMNS);
          }
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setConfigColumns(DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [org?.organization_id]);

  // Initialize URL parameters
  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!org?.organization_id) return;
    let cancelled = false;
    fetchDocumentTypes(org.organization_id)
      .then((data) => {
        if (cancelled) return;
        setDocumentTypes(asOptionList(data, ["documentType", "data"]));
      })
      .catch((error) => {
        console.error("Failed to load document types", error);
      });
    return () => {
      cancelled = true;
    };
  }, [org?.organization_id]);

  // Load data
  useEffect(() => {
    if (!hasInitializedURL.current || !org?.organization_id) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = mergeToolbarFilters(
          buildFiltersFromParams(searchParams),
          searchParams,
          DOCUMENT_TOOLBAR_FILTERS
        );
        const search = searchParams.get("search");
        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field: searchParams.get("sortBy") || "person_document_id",
            direction: searchParams.get("sortOrder") || "desc",
          },
          ...(search && search.trim() !== "" && { search: search.trim() }),
        };

        const filteredUrl = `${MAIN_URL}/api/organizations/${org.organization_id}/employee-documents-filtered`;
        const response = await fetch(filteredUrl, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
          signal: fetchControllerRef.current.signal,
        });

        let data = [];
        let useFallback = false;

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;
            if (result.pagination) {
              const pageSize =
                result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page:
                  result.pagination.page ||
                  Number.parseInt(searchParams.get("page"), 10) ||
                  1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
              });
            }
          } else {
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else if (response.status === 500 || response.status === 404 || response.status === 405) {
          // Server may not have indexWithFilters (e.g. old deployment); fallback to GET index
          useFallback = true;
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        if (useFallback) {
          const indexUrl = `${MAIN_URL}/api/organizations/${org.organization_id}/employemnt-document?per_page=all`;
          const indexRes = await fetch(indexUrl, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            signal: fetchControllerRef.current.signal,
          });
          if (!indexRes.ok) throw new Error(`Fallback failed: ${indexRes.status}`);
          const indexResult = await indexRes.json();
          const raw = indexResult?.document;
          const list = applyClientToolbarFilters(
            Array.isArray(raw) ? raw : (raw?.data ?? []),
            searchParams,
            DOCUMENT_TOOLBAR_FILTERS
          );
          const total = list.length;
          const pageSize = Number.parseInt(searchParams.get("pageSize"), 10) || 10;
          const page = Number.parseInt(searchParams.get("page"), 10) || 1;
          const start = (page - 1) * pageSize;
          data = list.slice(start, start + pageSize);
          setPaginationData({
            page,
            pageSize,
            total,
            totalPages: Math.max(1, Math.ceil(total / pageSize)),
            hasNext: page * pageSize < total,
            hasPrev: page > 1,
          });
        }

        const b = data.map((item) => {
          const op = item?.organization_person ?? item?.OrganizationPerson;
          const person = op?.person ?? op?.Person;
          const docId =
            item?.person_document_id ??
            item?.employee_document_id ??
            item?.id;
          const empName = person
            ? [person.first_name, person.middle_name, person.last_name].filter(Boolean).join(" ").trim()
            : (item?.Employee_name ?? item?.employee_name ?? "");
          return {
            ...item,
            id: docId,
            person_document_id: docId,
            Employee_name: empName || item?.Employee_name || "",
            employee_name: empName || item?.employee_name || item?.Employee_name || "",
            emp_code: item?.emp_code ?? op?.person_code ?? "",
            profile_image_url:
              item?.profile_image_url ||
              person?.profile_image_url ||
              "",
            document_type: item?.document_type_name ?? item?.document_type ?? (item?.document_type?.document_type_name ?? ""),
          };
        });

        setDocuments(b);
      } catch (error) {
        if (error.name === "AbortError") {
          return;
        }
        console.error("Error fetching employee documents:", error);
        toast.error("Failed to load employee documents");
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [searchParams, org?.organization_id]);

  let deleteStatus = async (id) => {
    try {
      const org_id = org.organization_id;
      const response = await axios.delete(
        `${MAIN_URL}/api/organizations/${org_id}/employemnt-document/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (response.status === 200) {
              toast.success(response.data.message);
              console.log("Employee Document deleted:", response.data.message);
            } else {
              const errorMessage =
                response.data.message ||
                response.data.errors?.[Object.keys(response.data.errors)[0]]?.[0] ||
                "Failed to delete Document";
      
              toast.error(errorMessage);
              console.warn("Deletion error:", response.status, response.data);
            }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session Expired!");
        window.location.href = "/login";
      }
      console.error("Delete failed:", error);
      toast.error(
        error.response?.data?.error || "Failed to delete Attendance Status Type"
      );
    }
  };

  const handleDelete = useCallback(
    async (id) => {
      try {
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org?.organization_id}/employemnt-document/${id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.ok) {
          const result = await response.json();
          toast.success(result.message || "Employee Document deleted successfully");
          const params = new URLSearchParams(searchParams);
          setSearchParams(params);
        } else {
          const errorData = await response.json();
          toast.error(errorData?.message || errorData?.error || "Failed to delete Employee Document");
        }
      } catch (error) {
        console.error("Delete failed:", error);
        toast.error("An unexpected error occurred while deleting.");
      }
    },
    [org?.organization_id, searchParams, setSearchParams]
  );

  const handleEdit = useCallback(
    (item) => {
      navigate(`/employee/documents/edit/${item.id}`);
    },

    [navigate]
  );



     const handleShow = useCallback(
      (item) => {
        navigate(`/employee/documents/view/${item.id}`)
      },
      [navigate],
    )
  

  return (
    <>
      <Layout4
        loading={loading}
        heading={"Employee Documents"}
        btnName={"Add Document"}
        add_action={"EMPLOYEE_DOCUMENT_ADD"}
        delete_action={"EMPLOYEE_DOCUMENT_DELETE"}
        Data={documents}
        tableHeaders={[
          {
            name: "Document Name",
            value_key: "attendance_status_type_name",
            textStyle: "capitalize",
          },
          {
            name: "Status Type Code",
            value_key: "attendance_status_type_code",
            textStyle: "capitalize",
          },
          {
            name: "Description",
            value_key: "description",
            textStyle: "capitalize",
          },
        ]}
        Icons={[
          <PersonIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
          <FormatAlignJustifyIcon color="primary" />,
          <CategoryIcon sx={{ color: "text.secondary" }} />,
          <DateRangeIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          "Attendance Status Type",
          "Attendance Status Type",
          "Add Attendance Status Type",
          "Attendance Status Type",
        ]}
        Route={"/employee/documents"}
        setData={setDocuments}
        DeleteFunc={deleteStatus}
      />

      {/* tableName=employee_documents is used as datagrid_key for organization-datagrid-user-views (saved views) */}
      <TableDataGeneric
        tableName="employee_documents"
        primaryKey="person_document_id"
        heading="Employee Documents"
        data={documents}
        paginationData={paginationData}
        filterModule="employee_documents"
        toolbarExtras={
          <ToolbarFilterSelect
            label="Document Type"
            urlKey="document_type_id"
            allLabel="All document types"
            options={documentTypes}
            valueKey="person_document_type_id"
            labelKey="document_type_name"
            searchParams={searchParams}
            onChange={handleToolbarFilterChange}
          />
        }
        sortname={"document_name"}
        showActions={true}
        Route="/employee/documents"
        DeleteFunc={handleDelete}
        edit_delete_action={["EMPLOYEE_DOCUMENT_DELETE", "EMPLOYEE_DOCUMENT_DELETE"]}
        EditFunc={handleEdit}
        handleShow={handleShow}
        token={localStorage.getItem("token")}
        configss={configColumns}
        {...(tableConfig && { config: tableConfig })}
      />
    </>
  );
}
